import {
  useEffect,
  useState,
  type ComponentProps,
  type ReactNode,
} from "react";
import { CommandInput, CommandItem } from "../command";
import { Tooltip, TooltipContent, TooltipTrigger } from "../tooltip";
import { cn } from "../../../lib/utils";
import { matchRanges, NEEDS_RECONNECT_LABEL } from "./account-menu-model";
import { SelectorAvatar, SelectorAvatarStack } from "./selector-avatar";
import type { SelectorAccount, SelectorGroup, SelectorSize } from "./types";

export type SelectionState = false | true | "indeterminate";

/** Declared in the search model so the row and the search haystack cannot
 * drift apart. */
export { NEEDS_RECONNECT_LABEL } from "./account-menu-model";

export function SelectorSearchInput(
  props: ComponentProps<typeof CommandInput>
) {
  return (
    <CommandInput
      leadingIcon={
        <i
          aria-hidden
          className="fa-regular fa-magnifying-glass size-5 shrink-0 text-center text-[16px] leading-5 text-neutral-500"
        />
      }
      {...props}
    />
  );
}

/**
 * The cmdk-backed search box for a single-select account menu. Controlled, so
 * the menu can filter through the shared model rather than cmdk's own scoring.
 */
export function MenuSearch({
  value,
  onValueChange,
  placeholder = "Search accounts...",
  size = "md",
  autoFocus = true,
}: {
  value: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  size?: SelectorSize;
  autoFocus?: boolean;
}) {
  return (
    <SelectorSearchInput
      value={value}
      onValueChange={onValueChange}
      placeholder={placeholder}
      autoFocus={autoFocus}
      wrapperClassName={cn(
        "shrink-0",
        size === "md" ? "h-10 px-3.5" : "h-8 px-3",
        "gap-2"
      )}
      className="h-full py-0 font-inter text-sm"
    />
  );
}

/** The matched slice of `text` wrapped in a `<mark>`. With no hit the text is
 * rendered as a single node, so a plain `getByText(label)` still resolves. */
export function Highlighted({
  text,
  query,
}: {
  text?: string;
  query?: string;
}) {
  if (!text) return null;
  const range = matchRanges(text, query);
  if (!range) return <>{text}</>;
  const [start, end] = range;
  // blue-100, not blue-50: the selected row is itself blue-50, so a highlight
  // painted in blue-50 vanishes on the very row the reader is looking at.
  return (
    <>
      {text.slice(0, start)}
      <mark className="rounded-sm bg-blue-100 text-inherit">
        {text.slice(start, end)}
      </mark>
      {text.slice(end)}
    </>
  );
}

export function selectionStateLabel(checked: SelectionState) {
  if (checked === "indeterminate") return "partially selected";
  return checked ? "selected" : "not selected";
}

/** A group-shaped row names its own mixed state: `aria-selected` has no
 * "mixed", so the accessible name has to carry it. */
export function groupRowAccessibleName(
  label: string,
  checked: SelectionState
): string {
  return checked === "indeterminate" ? `${label}, partially selected` : label;
}

export function MenuCheckbox({ checked }: { checked: SelectionState }) {
  return (
    <span
      aria-hidden
      data-state={
        checked === "indeterminate"
          ? "indeterminate"
          : checked
            ? "checked"
            : "unchecked"
      }
      className={cn(
        "flex size-4 shrink-0 items-center justify-center rounded-[4px] border shadow-xs",
        checked === false && "border-neutral-200 bg-white",
        checked === true && "border-blue-500 bg-blue-500 text-white",
        checked === "indeterminate" &&
          "border-blue-500 bg-blue-50 text-blue-500"
      )}
    >
      {checked === "indeterminate" ? (
        <i className="fa-solid fa-minus text-[10px] leading-none text-blue-500" />
      ) : checked ? (
        <i className="fa-solid fa-check text-[10px] leading-none text-white" />
      ) : null}
    </span>
  );
}

/** `aria-label` overrides the subtree, so everything the row shows has to be
 * repeated here or it becomes unreachable. */
export function accountRowAccessibleName(
  account: SelectorAccount,
  { selected, multi }: { selected: boolean; multi: boolean }
): string {
  return [
    account.label,
    account.secondaryText || undefined,
    account.disconnected ? NEEDS_RECONNECT_LABEL : undefined,
    !multi && selected ? "Selected" : undefined,
    account.disconnected ? account.attentionReason : undefined,
    account.disabledReason,
  ]
    .filter(Boolean)
    .join(". ");
}

/**
 * A multi-select disabled row's reason, on hover and while the arrow keys are
 * on the row. The row itself stays unselectable; this only explains why.
 */
function DisabledReasonTooltip({
  reason,
  keyboardNav,
  keyboardActive,
  children,
}: {
  reason: string;
  /** The keyboard is driving, so a stale hover must not open a second tip. */
  keyboardNav: boolean;
  keyboardActive: boolean;
  children: ReactNode;
}) {
  const [hovered, setHovered] = useState(false);
  useEffect(() => {
    if (keyboardNav) setHovered(false);
  }, [keyboardNav]);
  return (
    <Tooltip
      open={keyboardActive || (hovered && !keyboardNav)}
      onOpenChange={setHovered}
    >
      <TooltipTrigger
        asChild
        onPointerEnter={() => setHovered(true)}
        onPointerMove={() => setHovered(true)}
        onPointerLeave={() => setHovered(false)}
      >
        {children}
      </TooltipTrigger>
      <TooltipContent side="right" className="max-w-[240px] text-left">
        {reason}
      </TooltipContent>
    </Tooltip>
  );
}

export function AccountMenuItem({
  account,
  selected,
  multi = false,
  nested = false,
  size = "md",
  query,
  sectionId,
  id,
  active = false,
  keyboardNav = false,
  keyboardActive = false,
  onSelect,
}: {
  account: SelectorAccount;
  selected: boolean;
  multi?: boolean;
  nested?: boolean;
  size?: SelectorSize;
  query?: string;
  /** The group this row sits in, so ArrowLeft/ArrowRight can collapse the
   * section from any row inside it rather than only from its header. */
  sectionId?: string;
  /** Multi only. A listbox names its active option through
   * `aria-activedescendant`, which needs an id to point at. */
  id?: string;
  /** Multi only. The row the arrow keys are on. It is not DOM focus, because a
   * listbox is a single tab stop and its options are skipped by the browser. */
  active?: boolean;
  /** Multi only. The arrow keys, not the pointer, are driving the menu. */
  keyboardNav?: boolean;
  /** Multi only. `active` while the keyboard is driving, which is when a
   * disabled row's reason opens without a hover. */
  keyboardActive?: boolean;
  onSelect: () => void;
}) {
  const disabled = Boolean(account.disabled);
  const title = account.disabledReason || account.attentionReason;
  const reason = disabled ? title : undefined;
  // Single-select rows are cmdk items, which drop a ref and so cannot anchor a
  // Radix tooltip. They explain themselves through the native title instead.
  const showsTooltip = multi && Boolean(reason);
  const accessibleName = accountRowAccessibleName(account, {
    selected,
    multi,
  });
  const content = (
    <>
      {multi ? <MenuCheckbox checked={selected} /> : null}
      <span className="flex min-w-0 flex-1 items-center gap-2">
        <SelectorAvatar account={account} size={24} showAttention={false} />
        <span className="min-w-0 flex-1">
          <span className="flex items-baseline gap-2">
            <span className="min-w-0 flex-1 truncate text-sm font-medium leading-5 text-neutral-800">
              <Highlighted text={account.label} query={query} />
            </span>
            {account.disconnected ? (
              <span
                title={showsTooltip ? undefined : account.attentionReason}
                className="shrink-0 font-inter text-xs leading-4 font-medium text-warning-700"
              >
                {NEEDS_RECONNECT_LABEL}
              </span>
            ) : null}
          </span>
          {/* Always rendered, empty included: a row that grows a line when it
              happens to have a handle makes the list jump as you filter. */}
          <span
            className={cn(
              "block truncate text-xs leading-4",
              selected ? "text-blue-700" : "text-neutral-500"
            )}
          >
            <Highlighted text={account.secondaryText} query={query} />
          </span>
        </span>
      </span>
      {!multi && selected ? (
        <>
          <span className="sr-only">Selected</span>
          <i
            aria-hidden
            className="fa-regular fa-check ml-auto size-5 shrink-0 text-center text-[16px] leading-5 text-blue-500"
          />
        </>
      ) : null}
    </>
  );
  const className = cn(
    // 44px, two lines. Fixed height, because the second line is optional and a
    // list of rows that change height as you filter is unreadable.
    "relative flex h-11 w-full cursor-pointer items-center gap-2 rounded-none py-1.5 text-left font-inter text-sm font-medium text-neutral-800 outline-hidden select-none",
    size === "md" ? "px-3.5" : "px-3",
    nested && (size === "md" ? "pr-3.5 pl-[42px]" : "pr-3 pl-[38px]"),
    // `data-chosen` is the reader's choice; cmdk's `data-selected` is only the
    // row under the pointer or the arrow keys. Two attributes so these outrank
    // the one-attribute active rules below.
    "data-[chosen]:bg-blue-50 data-[chosen]:hover:bg-blue-50 data-[chosen]:data-[selected=true]:bg-blue-50",
    // The ring is gated on the shell's nav mode because cmdk gives the mouse
    // and the arrow keys the same highlight.
    "hover:bg-neutral-50",
    "data-[selected=true]:bg-neutral-50",
    "group-data-[nav=keyboard]/menu:data-[selected=true]:ring-1 group-data-[nav=keyboard]/menu:data-[selected=true]:ring-inset group-data-[nav=keyboard]/menu:data-[selected=true]:ring-blue-500",
    // `aria-disabled`, not `disabled`: a disabled element swallows the hover
    // that shows the reason. Clicks are refused in the handlers instead.
    "aria-disabled:cursor-not-allowed aria-disabled:opacity-50",
    !multi &&
      "data-[disabled=true]:pointer-events-auto data-[disabled=true]:cursor-not-allowed"
  );
  if (multi) {
    const row = (
      <button
        type="button"
        role="option"
        id={id}
        tabIndex={-1}
        aria-selected={selected}
        aria-disabled={disabled || undefined}
        data-chosen={selected || undefined}
        data-selected={active ? "true" : undefined}
        aria-label={accessibleName}
        title={reason ? undefined : title}
        data-section-id={sectionId}
        data-testid={account.testId}
        className={className}
        onClick={() => {
          if (!disabled) onSelect();
        }}
      >
        {content}
      </button>
    );
    return reason ? (
      <DisabledReasonTooltip
        reason={reason}
        keyboardNav={keyboardNav}
        keyboardActive={keyboardActive}
      >
        {row}
      </DisabledReasonTooltip>
    ) : (
      row
    );
  }

  return (
    <CommandItem
      // cmdk requires a unique value per item; two accounts can share a label.
      value={`${account.label} ${account.secondaryText || ""} ${account.searchText || ""} ${account.id}`}
      aria-label={accessibleName}
      data-section-id={sectionId}
      // cmdk overwrites aria-selected and data-selected with its own highlight
      // state, so the chosen row needs a channel of its own.
      data-chosen={selected || undefined}
      disabled={account.disabled}
      onSelect={onSelect}
      title={title}
      data-testid={account.testId}
      className={className}
    >
      {content}
    </CommandItem>
  );
}

/**
 * A section header. Sticky, counted, and collapsible; `interactive` turns it
 * into the group's own selectable row for menus where a group is a choice.
 */
export function GroupHeader({
  label,
  count,
  collapsed = false,
  onToggleCollapse,
  interactive = false,
  selected = false,
  disabled,
  disabledReason,
  caption,
  accounts,
  value,
  size = "md",
  query,
  onSelect,
  sectionId,
  className,
}: {
  label: string;
  count: number;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  interactive?: boolean;
  selected?: boolean;
  disabled?: boolean;
  disabledReason?: string;
  caption?: ReactNode;
  accounts?: SelectorAccount[];
  value?: string;
  size?: SelectorSize;
  query?: string;
  onSelect?: () => void;
  /** Read back off the highlighted element so ArrowLeft/ArrowRight know which
   * section to collapse. */
  sectionId?: string;
  className?: string;
}) {
  const chevron = onToggleCollapse ? (
    <button
      type="button"
      // Not a tab stop: a focusable non-option inside a listbox breaks the
      // single-tab-stop contract. The arrow keys are the keyboard route.
      tabIndex={-1}
      aria-label={`${collapsed ? "Expand" : "Collapse"} ${label}`}
      aria-expanded={!collapsed}
      onPointerDown={(event) => event.stopPropagation()}
      // cmdk's root swallows Enter and its preventDefault suppresses the click
      // a button would otherwise synthesise, so handle the keys here.
      onKeyDown={(event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        event.stopPropagation();
        onToggleCollapse();
      }}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onToggleCollapse();
      }}
      className="inline-flex size-5 shrink-0 items-center justify-center rounded text-neutral-500 hover:bg-neutral-50 hover:text-neutral-800"
    >
      <i
        aria-hidden
        className={cn(
          "fa-regular fa-chevron-down text-[12px] leading-none transition-transform",
          collapsed && "-rotate-90"
        )}
      />
    </button>
  ) : null;

  const body = (
    <>
      <span className="min-w-0 truncate">
        <Highlighted text={label} query={query} />
      </span>
      <span className="shrink-0 font-normal text-neutral-400">
        {`· ${count}`}
      </span>
      {interactive && collapsed ? (
        <SelectorAvatarStack
          accounts={accounts || []}
          max={3}
          size={20}
          className="ml-1"
        />
      ) : null}
      <span className="flex-1" />
      {caption ? (
        <span className="shrink-0 text-xs font-normal text-neutral-600">
          {caption}
        </span>
      ) : null}
      {interactive && selected ? (
        <>
          <span className="sr-only">Selected</span>
          <i
            aria-hidden
            className="fa-regular fa-check size-5 shrink-0 text-center text-[16px] leading-5 text-blue-500"
          />
        </>
      ) : null}
    </>
  );

  const sharedClassName = cn(
    // Sticky so the group a row belongs to stays legible while scrolling. The
    // list needs scroll-pt to match, or cmdk parks a row under this.
    "sticky top-0 z-10 flex w-full items-center gap-1.5 bg-white font-inter text-xs font-medium leading-4 text-neutral-500",
    size === "md" ? "h-8 px-3.5" : "h-7 px-3",
    className
  );

  if (!interactive) {
    return (
      <div
        role="presentation"
        data-group-header=""
        data-section-id={sectionId}
        className={sharedClassName}
      >
        {body}
        {chevron}
      </div>
    );
  }

  // The chevron is the option's SIBLING: an interactive descendant inside
  // `role="option"` breaks the listbox pattern. So the wrapper owns the sticky
  // positioning and the ground, and the option owns neither.
  return (
    <div
      className={cn(
        "sticky top-0 z-10 flex w-full items-center bg-white",
        "h-11",
        selected
          ? "bg-blue-50"
          : "hover:bg-neutral-50 has-[[data-selected=true]]:bg-neutral-50",
        "group-data-[nav=keyboard]/menu:has-[[data-selected=true]]:ring-1 group-data-[nav=keyboard]/menu:has-[[data-selected=true]]:ring-inset group-data-[nav=keyboard]/menu:has-[[data-selected=true]]:ring-blue-500",
        className
      )}
    >
      <CommandItem
        value={value || `__group__ ${label}`}
        disabled={disabled}
        title={disabledReason}
        aria-label={[
          label,
          count === 1 ? "1 account" : `${count} accounts`,
          selected ? "Selected" : undefined,
        ]
          .filter(Boolean)
          .join(", ")}
        data-group-header=""
        data-section-id={sectionId}
        data-chosen={selected || undefined}
        onSelect={() => onSelect?.()}
        className={cn(
          "flex min-w-0 flex-1 items-center gap-1.5 rounded-none font-inter text-xs leading-4 font-medium text-neutral-500",
          "h-11",
          size === "md" ? "px-3.5" : "px-3",
          chevron ? "pr-1" : undefined,
          "cursor-pointer outline-hidden select-none"
        )}
      >
        {body}
      </CommandItem>
      {chevron ? (
        <span className={cn("shrink-0", size === "md" ? "pr-3.5" : "pr-3")}>
          {chevron}
        </span>
      ) : null}
    </div>
  );
}

/** Shown when a query matches nothing. Fixed minimum height so the popover does
 * not collapse to a sliver between keystrokes. */
export function MenuEmptyState({
  query,
  onClear,
  message = "No accounts found.",
  clearLabel = "Clear search",
}: {
  query?: string;
  onClear?: () => void;
  message?: string;
  clearLabel?: string;
}) {
  const trimmed = (query || "").trim();
  return (
    <div
      role="presentation"
      className="flex min-h-[120px] flex-col items-center justify-center gap-2 px-4 py-6 text-center"
    >
      <span className="font-inter text-sm text-neutral-500">
        {trimmed ? `No accounts match "${trimmed}"` : message}
      </span>
      {trimmed && onClear ? (
        <button
          type="button"
          onClick={onClear}
          className="font-inter text-sm font-medium text-blue-500 underline-offset-2 hover:underline"
        >
          {clearLabel}
        </button>
      ) : null}
    </div>
  );
}

export function GroupMenuItem({
  group,
  selected,
  checkbox,
  size = "md",
  twoLine = Boolean(group.caption),
  onSelect,
  children,
}: {
  group: SelectorGroup;
  selected?: boolean;
  checkbox?: SelectionState;
  size?: SelectorSize;
  /** 44px rows. A menu sets it on every row when any row has a caption, so
   * row heights do not vary within one list. */
  twoLine?: boolean;
  onSelect: () => void;
  children?: ReactNode;
}) {
  return (
    <CommandItem
      value={`${group.label} ${group.id}`}
      disabled={group.disabled}
      onSelect={onSelect}
      title={group.disabledReason}
      aria-label={
        checkbox !== undefined
          ? [group.label, group.caption, selectionStateLabel(checkbox)]
              .filter(Boolean)
              .join(", ")
          : undefined
      }
      className={cn(
        "w-full cursor-pointer rounded-none font-inter text-sm font-medium text-neutral-800",
        twoLine
          ? cn("h-11 py-1.5", size === "md" ? "px-3.5" : "px-3")
          : size === "md"
            ? "h-10 px-3.5 py-2.5"
            : "h-8 px-3 py-1.5"
      )}
    >
      {checkbox !== undefined ? <MenuCheckbox checked={checkbox} /> : null}
      {/* The caption sits under the name, never beside it: a trailing caption
          in a narrow menu squeezed the name to nothing. */}
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="flex min-w-0 items-center gap-2">
          <span className="min-w-0 truncate leading-5">{group.label}</span>
          <SelectorAvatarStack
            accounts={group.accounts || []}
            max={4}
            size={20}
          />
        </span>
        {/* Rendered empty on two-line rows without a caption, so names line
            up across the list. */}
        {twoLine ? (
          <span className="block truncate text-xs leading-4 font-normal text-neutral-600">
            {group.caption}
          </span>
        ) : null}
      </span>
      {checkbox === undefined && selected ? (
        <>
          <span className="sr-only">Selected</span>
          <i
            aria-hidden
            className="fa-regular fa-check ml-auto size-5 text-center text-[16px] leading-5 text-blue-500"
          />
        </>
      ) : null}
      {children}
    </CommandItem>
  );
}
